import { Typography } from '@mui/material';
import { createUnit, type AdminUnit } from '../../lib/units-api';
import { importRequest, parseUnitImport, unitTemplateCsv } from '../../lib/unit-import';
import { unitAddress } from '../../lib/unit-address';
import { CsvImportPanel } from '../../components/CsvImportPanel';
import { useT } from '../../i18n';

const TYPE_LABELS: Record<string, string> = { HOUSE: 'units.typeHouse', APARTMENT: 'units.typeApartment', RUKO: 'units.typeRuko' };
const OCCUPANCY_LABELS: Record<string, string> = { OCCUPIED: 'units.occupiedChip', VACANT: 'units.vacantChip' };

/** The "Impor massal" tab of the new-unit dialog: one unit per row, created one request each. */
export function UnitImportPanel({
  existing,
  onClose,
  onImported,
}: {
  existing: AdminUnit[];
  onClose: () => void;
  /** Some units were created; the list behind the dialog should reload. */
  onImported: () => void;
}) {
  const t = useT();
  return (
    <CsvImportPanel
      templateFile="template-impor-unit.csv"
      templateCsv={unitTemplateCsv}
      step1Help={t('units.import.step1Help')}
      parse={(text) => {
        const res = parseUnitImport(text, existing);
        if (res.ok) return res.rows;
        return t(res.error === 'empty' ? 'import.fileEmpty' : 'units.import.fileNoNumber');
      }}
      columns={[
        {
          label: t('units.colUnit'),
          render: (r) => (
            <Typography variant="body2" component="div" sx={{ whiteSpace: 'nowrap' }}>
              {r.number ? unitAddress(r.block, r.number) : r.block || '—'}
              {r.label ? <Typography variant="caption" color="text.secondary" component="div">{r.label}</Typography> : null}
            </Typography>
          ),
        },
        { label: t('units.colType'), render: (r) => t(TYPE_LABELS[r.type] ?? r.type) },
        { label: t('units.colOccupancy'), render: (r) => t(OCCUPANCY_LABELS[r.occupancy] ?? r.occupancy) },
      ]}
      describeRow={(r) => unitAddress(r.block, r.number)}
      send={(r) => createUnit(importRequest(r))}
      runLabel={(count) => t('units.import.run', { count })}
      sentLabel={(count) => t('units.import.added', { count })}
      onClose={onClose}
      onImported={onImported}
    />
  );
}
